/** @format */

import { AnalyticsService, BreakpointsService, UtilityService } from '@shared/data-access/services';
import {
	ChangeDetectionStrategy,
	Component,
	ComponentRef,
	computed,
	effect,
	inject,
	input,
	signal,
	TemplateRef,
	viewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DynamicDrawerSizes, ProductSelectionUtilityService } from '@pdp/utils';
import {
	HmfActionTextComponent,
	HmfDrawerComponent,
	HmfModalFactoryService,
	HmfSkeletonComponent
} from '@dsg-tech/homefield-angular';
import { HomrDataService, HomrPage } from '@shared/homr/data-access';
import { FeedbackComponent } from '../feedback/feedback.component';
import { KrakenComponent } from '@shared/components';
import { BreakPointNew } from '@shared/data-access/enums';
import { chain } from '@core/chain';
import { take } from 'rxjs/operators';

@Component({
	selector: 'pdp-component-size-chart-v2',
	templateUrl: './size-chart.component.html',
	styleUrl: './size-chart.component.scss',
	imports: [FeedbackComponent, HmfActionTextComponent, HmfSkeletonComponent, KrakenComponent],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SizeChartComponent {
	private readonly homrDataService = inject(HomrDataService);
	private readonly utilityService = inject(UtilityService);
	private readonly pdslUtilityService = inject(ProductSelectionUtilityService);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly sanitizer = inject(DomSanitizer);
	private readonly modalFactoryService = inject(HmfModalFactoryService);
	private readonly breakpointsService = inject(BreakpointsService);

	readonly sizeChartName = input<string>();

	private sizeChartRef: ComponentRef<HmfDrawerComponent>;
	sizeChartContent = viewChild.required<TemplateRef<unknown>>('sizeChartContent');

	heading = 'Size Chart';
	readonly sizeChartSurveyId = chain.sizeChartSurveyId;

	readonly displayHtml = signal<SafeHtml | null>(null);
	readonly krakenContent = signal<Record<string, Record<string, string>>[] | null>(null);
	readonly loading = signal(true);

	private readonly emsName = computed(() =>
		this.utilityService.convertSizeChartNameToNewFormat(this.sizeChartName())
	);
	readonly isAem = computed(() => this.emsName().includes('aem'));

	readonly drawerDirection = computed(() =>
		this.breakpointsService.breakpoint() <= BreakPointNew.SMALL ? 'bottom' : 'right'
	);

	constructor() {
		// When we update to Angular v20, we should update the modal service to make use of input binding instead
		// so we can just do that when we create the drawer instead of having to manually setInput in an effect
		// https://github.com/angular/angular/pull/60137
		effect(() => {
			const breakpoint = this.breakpointsService.breakpoint();
			if (this.sizeChartRef) {
				this.sizeChartRef.setInput(
					'size',
					this.pdslUtilityService.getValueByScreenSize(DynamicDrawerSizes, breakpoint)
				);
				this.sizeChartRef.setInput('direction', this.drawerDirection());
			}
		});
	}

	getSizeChart() {
		if (this.isAem()) {
			const url = `${this.getFirstLetter(this.sizeChartName())}/${this.sizeChartName()}/master.html`;
			//To get this call working locally, use CORS browser extension
			this.homrDataService.getAemData$(url).subscribe({
				next: (res) => {
					this.loading.set(false);
					this.displayHtml.set(this.sanitizer.bypassSecurityTrustHtml(res.toString()));
				},
				error: () => {
					this.loading.set(false);
				}
			});
		} else {
			this.homrDataService
				.getData$('size-chart', 3, this.emsName())
				.pipe(take(1))
				.subscribe({
					next: (homrTemplate: HomrPage) => {
						if (homrTemplate?.content['Kraken Alpha']) {
							this.krakenContent.set(homrTemplate.content['Kraken Alpha']);
						} else {
							this.loading.set(false);
						}
					},
					error: () => {
						this.loading.set(false);
					}
				});
		}
		this.openSizeChart();
		this.sendAnalyticsFor('Size Chart');
	}

	getFirstLetter(brand: string): string {
		const words = brand.split('_');
		const firstWord = words[0].toLowerCase();
		const secondWord = words[1];
		if (firstWord.includes('aem')) {
			return secondWord.charAt(0).toLowerCase();
		}
		return firstWord.charAt(0).toUpperCase();
	}

	openSizeChart() {
		if (!this.sizeChartRef) {
			this.sizeChartRef = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					headerText: this.heading,
					closable: true,
					direction: this.drawerDirection(),
					isCloseButtonSticky: true,
					size: this.pdslUtilityService.getValueByScreenSize(
						DynamicDrawerSizes,
						this.breakpointsService.breakpoint()
					)
				},
				this.sizeChartContent()
			);
		}

		this.sizeChartRef.instance.openDrawer();
	}

	sendAnalyticsFor(name: string) {
		/*
		 * Analytics Event
		 */
		// Set the modal name
		this.analyticsService.setModalName(name);

		// Set the Analytics event type
		this.analyticsService.setType('event', 'ModalOpen');

		// Trigger the Analytics update
		this.analyticsService.triggerAnalyticsEvent('reporting:update');
	}

	handleKrakenLoaded() {
		this.loading.set(false);
	}
}
